import React, { useContext, useMemo, createRef, useEffect } from 'react'
import { IPublicProfileProps } from './Interfaces'
import { Box, Grid, Typography, useMediaQuery } from '@material-ui/core'
import {
  Background,
  PointrLogo,
  Section,
  DidYouKnowPageDisplay,
  Top5PageDisplay,
  PursuitsPageDisplay,
  MoreAboutMePageDisplay,
  ImGoodAtPageDisplay,
  BasicsPageDisplay,
  MediaPageDisplay,
  TalentInitiatives
} from 'components'
import { theme } from 'config/constants/theme/theme'
import { MainLayout } from 'layout'
import Provider, { PublicProfileContext, PublicProfileContextProvider } from './Provider'
import { MENU_IDS, MENU_LIST } from 'config/constants/navigation-menu'
import { useStyles } from './Styles'
import { SnapshotPage } from 'smart-components'
import { WordBankCategory } from 'types'
import { get, orderBy, isEmpty } from 'lodash'

const PublicProfilePage: React.FC<IPublicProfileProps> = () => {
  const { state } = useContext<PublicProfileContext>(PublicProfileContextProvider)
  const classes = useStyles({})
  const mobileView = useMediaQuery(theme.breakpoints.down('md'))

  const total = get(state, 'validation.total', {})
  const medias = get(state, 'user.medias', {})

  const hideMenuItem = {
    [MENU_IDS.TOP_FIVES]: !total.isTop5SComplete,
    [MENU_IDS.DID_YOU_KNOW]: !total.isDidYouKnowSemiComplete,
    [MENU_IDS.BASICS]: !total.isTheBasicsComplete,
    [MENU_IDS.I_PURSUE_WITH_INTENSITY]: !total.isIPursuitComplete,
    [MENU_IDS.IM_GOOD_AT]: !total.isImGoodAtComplete,
    [MENU_IDS.MORE_ABOUT_ME]: !total.isMoreAboutMeComplete,
    [MENU_IDS.MEDIA]: !medias || !medias.length,
    [MENU_IDS.INITIATIVES]: isEmpty(state?.initiatives)
  }

  const getChildren = (id: string, isLastChildren: boolean) => {
    switch (id) {
      case MENU_IDS.TOP_FIVES:
        return (
          <Top5PageDisplay
            wordBanks={state?.wordBanks}
            userWordBanks={{
              [WordBankCategory.CompanyDescriptor]: state?.responses?.thriveEnv,
              [WordBankCategory.Xfactor]: state?.responses?.teamMembersSay,
              [WordBankCategory.TalentTags]: state?.responses?.selfTag
            }}
          />
        )
      case MENU_IDS.BASICS:
        return (
          <BasicsPageDisplay
            firstName={state?.user?.firstName}
            lastName={state?.user?.lastName}
            avatar={state?.user?.avatar}
            hometownLocation={state?.responses?.hometownLocation}
            currentRole={state?.responses?.currentRole}
            currentCompany={state?.responses?.currentCompany}
            schools={state?.responses?.schools}
          />
        )
      case MENU_IDS.DID_YOU_KNOW:
        return (
          <DidYouKnowPageDisplay
            projectToLife={state?.responses?.projectToLife}
            funFact={state?.responses?.funFact}
            statNum={state?.responses?.statNum}
            statDetail={state?.responses?.statDetail}
            randomJob={state?.responses?.randomJob}
            uniqueInterest={state?.responses?.uniqueInterest}
            tedTalk={state?.responses?.tedTalk}
          />
        )
      case MENU_IDS.IM_GOOD_AT:
        return (
          <ImGoodAtPageDisplay
            expertise={state?.responses?.expertise}
            becomeExpertIn={state?.responses?.becomeExpertIn}
            lastChildren={isLastChildren}
          />
        )
      case MENU_IDS.I_PURSUE_WITH_INTENSITY:
        return <PursuitsPageDisplay passions={state?.responses?.passions} />
      case MENU_IDS.MORE_ABOUT_ME:
        return (
          <MoreAboutMePageDisplay
            impactOn={state?.responses?.impactOn}
            impactGoodAt={state?.responses?.impactGoodAt}
            impactInterested={state?.responses?.impactInterested}
            toGetMeTalking={state?.responses?.toGetMeTalking}
            inDealingWithPeople={state?.responses?.inDealingWithPeople}
            isLastChildren={isLastChildren}
          />
        )
      case MENU_IDS.MEDIA:
        return <MediaPageDisplay medias={medias} />
      case MENU_IDS.INITIATIVES:
        return <TalentInitiatives initiatives={state?.initiatives} />
      case MENU_IDS.SNAPSHOT:
      default:
        return (
          <div className={classes.resetPaddingBottom}>
            <SnapshotPage {...state} />
          </div>
        )
    }
  }

  const filteredMenu = orderBy(
    MENU_LIST.filter(menu => !hideMenuItem[menu.id]),
    page => page.id !== MENU_IDS.SNAPSHOT && page.id !== MENU_IDS.DID_YOU_KNOW
  )

  const pages = filteredMenu.map((page, index) => {
    const displayComponent = getChildren(page.id, filteredMenu.length - 1 === index)
    return { ...page, displayComponent }
  })

  const sections = useMemo(() => Array.from({ length: MENU_LIST.length }).map(() => createRef<HTMLDivElement>()), [])

  useEffect(() => {
    if (!mobileView) {
      for (const section of sections) {
        if (section?.current?.id === window.location.hash.replace('#', '')) {
          const top = section.current.getBoundingClientRect().top + window.pageYOffset
          window.scrollTo({ top })
        }
      }
    }
  }, [])

  return (
    <Grid container className={classes.root}>
      {state.user && state.responses && state?.validation?.total?.isMiniProfileComplete ? (
        <MainLayout hideMenuItem={hideMenuItem} pages={pages} {...state}>
          <Box>
            {pages.map((page, index) => (
              <Box key={page.id} id={page.id} className={classes.pageSection} ref={sections[index]}>
                <Box display="flex">{getChildren(page.id, pages.length - 1 === index)}</Box>
              </Box>
            ))}
          </Box>
        </MainLayout>
      ) : (
        <Grid container>
          <Background />
          <Box className={classes.content}>
            <Box display="flex">
              <Grid container>
                <Grid item hidden={!mobileView} className={classes.logoContainer}>
                  <PointrLogo />
                </Grid>
                <Grid item className={classes.contentGrid}>
                  <Section
                    size={{ height: 200, width: 850 }}
                    background={'#413292'}
                    padding={'30px 20px'}
                    direction={'column'}
                  >
                    <Typography id="public-profile-loading-label" className={classes.h3} variant="h3">
                      {state.error || 'Loading...'}
                    </Typography>
                  </Section>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
      )}
    </Grid>
  )
}

export default (props: IPublicProfileProps) => (
  <Provider {...props}>
    <PublicProfilePage {...props} />
  </Provider>
)
